import { WatchLaterOutlined } from '@mui/icons-material';
import {
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import dayjs from 'dayjs';
import { Visitor } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { Control, Controller, UseFormSetValue, useWatch } from 'react-hook-form';
import { isMaxNumberOfUsersReached } from 'utils/VisitorGroup/VisitorGroup';
import useModalContext from 'v2/components/Modal/useModalContext';
import { UserFormVisitorGroup } from 'v2/hooks/useUserFormVisitorGroups/useUserFormVisitorGroups';
import { formatDate } from 'v2/utils/dateTime';
import { EditVisitorGroupsFormValues } from '../drawers/EditVisitorGroupsDrawer';
import ExpirationDateModal from './ExpirationDateModal';

interface Props {
  control: Control<EditVisitorGroupsFormValues>;
  setValue: UseFormSetValue<EditVisitorGroupsFormValues>;
  coContractors: UserFormVisitorGroup[];
  visitor: Visitor;
}

const VisitorGroupsSection = ({ control, setValue, coContractors, visitor }: Props) => {
  const { translation } = useTranslation();
  const { openModal } = useModalContext();

  const expirationByGroupId = useWatch({ control, name: 'expirationByGroupId' });

  const handleOpenExpirationDateModal = (visitorGroup: UserFormVisitorGroup) => {
    const currentExpirationDate = expirationByGroupId[visitorGroup.id];
    openModal(
      <ExpirationDateModal
        visitorName={visitor.name}
        groupName={visitorGroup.name}
        initialExpirationDate={currentExpirationDate ? dayjs(currentExpirationDate) : null}
        onSave={(expirationDate) =>
          setValue(
            'expirationByGroupId',
            { ...expirationByGroupId, [visitorGroup.id]: expirationDate ? expirationDate.toISOString() : null },
            { shouldDirty: true }
          )
        }
      />
    );
  };

  return (
    <Controller
      name="selectedGroupIds"
      control={control}
      render={({ field }) => {
        const toggleGroup = (groupId: string, checked: boolean) => {
          field.onChange(checked ? [...field.value, groupId] : field.value.filter((id) => id !== groupId));
          if (!checked) {
            const nextExpirationByGroupId = { ...expirationByGroupId };
            delete nextExpirationByGroupId[groupId];
            setValue('expirationByGroupId', nextExpirationByGroupId, { shouldDirty: true });
          }
        };

        return (
          <FormControl component="fieldset">
            <FormLabel component="legend">{translation.user_groups}</FormLabel>
            <FormGroup>
              {coContractors.map((group) => {
                const expirationDate = expirationByGroupId[group.id];
                const isSelected = field.value.includes(group.id);
                const isDisabled = !isSelected && isMaxNumberOfUsersReached(group);
                return (
                  <Stack key={group.id} direction="row" alignItems="center" justifyContent="space-between">
                    <Tooltip title={isDisabled ? translation.group_is_full : ''}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            id={`selected-group-id-${group.id}-checkbox`}
                            checked={isSelected}
                            onChange={(_ev, checked) => toggleGroup(group.id, checked)}
                            disabled={isDisabled}
                          />
                        }
                        label={<Typography>{group.name}</Typography>}
                      />
                    </Tooltip>
                    {isSelected && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color={expirationDate ? 'primary' : 'default'}
                        icon={<WatchLaterOutlined />}
                        label={expirationDate ? formatDate(expirationDate) : translation.set_expiration}
                        onClick={() => handleOpenExpirationDateModal(group)}
                        sx={{ cursor: 'pointer' }}
                      />
                    )}
                  </Stack>
                );
              })}
            </FormGroup>
          </FormControl>
        );
      }}
    />
  );
};

export default VisitorGroupsSection;
